import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { RELEASES } from "@/lib/releases";
import { RELEASE_MOTION } from "@/lib/motion";
import GhostLotus from "@/components/GhostLotus";
import DriftingField from "@/components/DriftingField";
import { PaisleyRule } from "@/components/Mughal";

const MusicPage = () => (
  <main data-testid="music-page" className="relative min-h-screen overflow-hidden bg-[#f6f1e5]">
    <DriftingField images={RELEASES.map((r) => r.cover)} count={8} opacity={0.07} size={200} />
    <header className="relative z-10 mx-auto flex w-full max-w-6xl items-center justify-between px-6 pt-10 md:px-10">
      <Link
        data-testid="music-back-link"
        to="/"
        className="text-xs uppercase text-[#7a7160] transition-colors duration-500 hover:text-[#8a6a1f]"
        style={{ letterSpacing: "0.3em" }}
      >
        &larr; Amrita Sen
      </Link>
      <a
        data-testid="music-spotify-link"
        href="https://open.spotify.com/artist/21n4V78skCmFZRgMxtLic0"
        target="_blank"
        rel="noopener noreferrer"
        className="text-xs uppercase text-[#7a7160] transition-colors duration-500 hover:text-[#8a6a1f]"
        style={{ letterSpacing: "0.3em" }}
      >
        Spotify
      </a>
    </header>

    <div className="relative z-10 mx-auto w-full max-w-6xl px-6 pb-8 pt-14 md:px-10 md:pt-20">
      <GhostLotus className="absolute -top-10 right-0 h-[46vh] w-auto text-[#8a6a1f] opacity-[0.1] md:right-10" />
      <motion.h1
        data-testid="music-title"
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2, ease: "easeOut" }}
        className="font-masthead gold-text relative uppercase leading-none"
        style={{ fontSize: "clamp(3rem, 9vw, 8rem)", letterSpacing: "0.09em", fontWeight: 500 }}
      >
        Music
      </motion.h1>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8, duration: 1.2 }}
        className="font-serif-display mt-4 italic text-[#7a7160]"
        style={{ fontSize: "clamp(0.95rem, 1.4vw, 1.3rem)", letterSpacing: "0.28em" }}
      >
        every release, gathered
      </motion.p>
      <PaisleyRule className="mt-8 max-w-md text-[#8a6a1f]/50" />
    </div>

    <section
      data-testid="releases-grid"
      className="relative z-10 mx-auto grid w-full max-w-6xl grid-cols-2 gap-4 px-6 pb-32 pt-10 sm:grid-cols-3 md:grid-cols-4 md:gap-5 md:px-10 lg:grid-cols-5"
    >
      {RELEASES.map((r, i) => (
        <motion.a
          key={r.href}
          data-testid={`release-${i}`}
          href={r.href}
          target="_blank"
          rel="noopener noreferrer"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-30px" }}
          transition={{ duration: 0.8, delay: (i % 5) * 0.08, ease: "easeOut" }}
          className="group block"
        >
          <div
            className="group/cover relative overflow-hidden transition-all duration-500 group-hover:-translate-y-1"
            style={{ boxShadow: "0 0 0 1px rgba(33,29,21,0.1)" }}
            onMouseEnter={(e) => e.currentTarget.querySelector("video")?.play().catch(() => {})}
            onMouseLeave={(e) => {
              const v = e.currentTarget.querySelector("video");
              if (v) v.pause();
            }}
          >
            <img
              src={r.cover}
              alt={`${r.title} — cover art`}
              loading="lazy"
              className="aspect-square w-full object-cover transition-all duration-700 group-hover:scale-[1.04]"
            />
            {RELEASE_MOTION[r.title] && (
              <video
                data-testid={`release-motion-${i}`}
                muted
                loop
                playsInline
                preload="none"
                className="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-700 group-hover/cover:opacity-100"
              >
                <source src={`/motion/${RELEASE_MOTION[r.title]}.mp4`} type="video/mp4" />
                <source src={`/motion/${RELEASE_MOTION[r.title]}.webm`} type="video/webm" />
              </video>
            )}
          </div>
          <div className="mt-3 flex items-baseline justify-between gap-2">
            <span className="font-serif-display text-base font-light text-[#211d15]/75 transition-colors duration-500 group-hover:text-[#8a6a1f]">
              {r.title}
            </span>
            <span className="text-[10px] uppercase text-[#7a7160]/60" style={{ letterSpacing: "0.2em" }}>
              {r.year}
            </span>
          </div>
        </motion.a>
      ))}
    </section>

    <div className="noise-overlay" aria-hidden="true" />
  </main>
);

export default MusicPage;
